import { Claim } from '@wsphere/warranties/domain';

const Outcome = Claim.ClaimItem.Eligibility.Outcome;

/** Static copy for a job's eligibility check. */
export const CLAIM_ITEM_ELIGIBILITY_CONFIG = {
  title: 'Eligibility',
  check: 'Check eligibility',
  checkAgain: 'Check again',
  checking: 'Checking...',
  notChecked: "This job's eligibility hasn't been checked yet.",
  version: 'Version',
  someone: 'Someone',
  checkedBy: (name: string) => `Checked by ${name}`,
  outOfDate: 'The claim or job changed since this check. Check again before approving.',
  noFindings: 'Every check passed.',
  expected: 'Expected',
  found: 'Found',
  earlier: 'Earlier checks',
  showEarlier: (count: number) => `Show ${count} earlier ${count === 1 ? 'check' : 'checks'}`,
  hideEarlier: 'Hide earlier checks',
  earlierDetails: {
    expand: 'Show why',
    collapse: 'Hide why',
    lossDate: 'Loss date',
  },
  loadFailed: "We couldn't load this job's eligibility. Reload to try again.",

  outcomes: {
    [Outcome.ELIGIBLE]: 'Eligible',
    [Outcome.LIMITED]: 'Limited',
    [Outcome.EXCLUDED]: 'Excluded',
    [Outcome.INELIGIBLE]: 'Ineligible',
    [Outcome.INSUFFICIENT_INFORMATION]: 'Needs information',
  } as Record<Claim.ClaimItem.Eligibility.Outcome, string>,

  // The outcome as a sentence, after a check.
  results: {
    [Outcome.ELIGIBLE]: 'This job is eligible.',
    [Outcome.LIMITED]: 'This job is eligible up to its remaining limit.',
    [Outcome.EXCLUDED]: 'This job is excluded.',
    [Outcome.INELIGIBLE]: 'This job is ineligible.',
    [Outcome.INSUFFICIENT_INFORMATION]: 'This job is missing information.',
  } as Record<Claim.ClaimItem.Eligibility.Outcome, string>,

  // The outcome as a state, in "while it's …".
  states: {
    [Outcome.ELIGIBLE]: 'eligible',
    [Outcome.LIMITED]: 'limited',
    [Outcome.EXCLUDED]: 'excluded',
    [Outcome.INELIGIBLE]: 'ineligible',
    [Outcome.INSUFFICIENT_INFORMATION]: 'missing information',
  } as Record<Claim.ClaimItem.Eligibility.Outcome, string>,

  limits: {
    deductible: 'Deductible',
    remaining: 'Remaining limit',
    perClaim: 'Limit per claim',
    group: 'set by a liability group',
    prior: 'Already paid on this coverage',
    requested: 'Requested',
  },

  // On a Limited result, so "Approved" and "Limited" don't read as a clash.
  approved: {
    within: (approved: string, requested: string, left: string) =>
      `Approved ${approved} of ${requested}, within the ${left} left`,
    over: (approved: string, requested: string, left: string) =>
      `Approved ${approved} of ${requested}, over the ${left} left`,
  },

  usage: {
    summary: (used: string, limit: string) => `${used} used since the sale, limit ${limit}`,
    limitOnly: (limit: string) => `Limit ${limit}`,
    passed: 'Within the limit',
    failed: 'Over the limit',
    missing: 'Missing information',
  },

  // Why Approve is unavailable in the decision dialog.
  approval: {
    NOT_EVALUATED: 'Check eligibility before approving this job.',
    OUT_OF_DATE: 'The claim or job changed since eligibility was checked. Check again before approving.',
    NOT_ELIGIBLE: (state: string) => `This job can't be approved while it's ${state}.`,
    OVER_LIMIT: (limit: string) => `The authorized total can't be more than the eligible limit of ${limit}.`,
  },

  toasts: {
    checked: (text: string) => ({ title: 'Eligibility checked', text }),
    failedTitle: "Couldn't check eligibility",
  },
} as const;
